import type React from 'react';
import { useCallback, useMemo } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { useFlexLayout, useTable } from 'react-table';
import classnames from 'classnames';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { emptyArray } from 'ui-components/default-props/empty';
import commonMessages from '../common-messages';
import { doubleDash } from '../../constants';
import { getTotalCount } from '../helper';

import style from '../common-styles.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const CLASS = 'class';
const QTY = 'qty';
const SHARE = 'share';

const dataSourcesToAlign = [QTY, SHARE];

const messages: Record<string, MessageDescriptor> = defineMessages({
  title: {
    defaultMessage: 'Shipments by Class',
    id: 'pn2+HZ',
    description: 'shipments by class table header',
  },
  class: {
    defaultMessage: 'Class',
    id: '8HGC+x',
    description: 'shipments by class table class header',
  },
  qty: {
    defaultMessage: 'Qty',
    id: 'r2acp/',
    description: 'shipments by class table quantity (abbreviated) header',
  },
  share: {
    defaultMessage: 'Share',
    id: 'qaUibY',
    description: 'shipments by class table share header',
  },
  economy: {
    defaultMessage: 'Economy',
    id: '/l8VMl',
    description: 'label for a type of shipping class',
  },
  expedited: {
    defaultMessage: 'Expedited',
    id: 'flTqOy',
    description: 'label for a type of shipping class',
  },
  overnight: {
    defaultMessage: 'Overnight',
    id: 'JTPmtO',
    description: 'label for a type of shipping class',
  },
  standard: {
    defaultMessage: 'Standard',
    id: 'iKkLPL',
    description: 'label for a type of shipping class',
  },
  other: {
    defaultMessage: 'Other',
    id: 'QWpW0l',
    description: 'label for a miscellaneous shipping class',
  },
});

type ShippingClassData = {
  carrierId?: number;
  name: string;
  count: number;
};

type ShipmentsByClassTableProps = {
  data?: ShippingClassData[];
};

export const ShipmentsByClassTable = ({ data = emptyArray as ShippingClassData[] }: ShipmentsByClassTableProps) => {
  const intl = useIntl();
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const emptyRow = useMemo(() => (!data.length ? <FormattedMessage {...commonMessages.nullStateMessage} /> : null), [data.length]);

  const renderAlignedCell = useCallback(
    (value: React.ReactNode, accessor: string) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {value}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{value}</div>
      ) : (
        value
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const renderAlignedHeader = useCallback(
    (accessor: string, title: React.ReactNode) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {title}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{title}</div>
      ) : (
        title
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const columns = useMemo(
    () => [
      {
        Header: () => renderAlignedHeader(CLASS, intl.formatMessage(messages[CLASS])),
        accessor: CLASS as typeof CLASS,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, CLASS),
      },
      {
        Header: () => renderAlignedHeader(QTY, intl.formatMessage(messages[QTY])),
        accessor: QTY as typeof QTY,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, QTY),
      },
      {
        Header: () => renderAlignedHeader(SHARE, intl.formatMessage(messages[SHARE])),
        accessor: SHARE as typeof SHARE,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, SHARE),
      },
    ],
    [intl, renderAlignedCell, renderAlignedHeader],
  );

  const tableData = useMemo(() => {
    const totalCount = getTotalCount(data);
    return data.map((shippingClass) => ({
      class: intl.formatMessage(messages[shippingClass.name.toLowerCase()]),
      qty: totalCount > 0 ? intl.formatNumber(shippingClass.count) : doubleDash,
      share:
        totalCount > 0
          ? intl.formatNumber(shippingClass.count / totalCount, {
              style: 'percent',
            })
          : doubleDash,
    }));
  }, [data, intl]);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div
        className={classnames({
          [style['table-title']]: !isShipStationAnalyticsFeatureEnabled,
          [analyticsStyle['analytics-table-title']]: isShipStationAnalyticsFeatureEnabled,
        })}
      >
        <Text textType={TextType.H3} value={messages.title} />
      </div>
      <ReactTableWrapper
        className={classnames({
          [analyticsStyle['analytics-table']]: isShipStationAnalyticsFeatureEnabled,
        })}
        emptyRow={emptyRow}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};

export default ShipmentsByClassTable;
